// A repertoire: its line, walked from the board a move at a time; what your
// games keep doing instead (habits), what your opponents play that the line
// does not cover (gaps), and the replies that lose for them (traps). The
// tree of every move is for strong players, behind a setting.

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { errorText, getJson, isSignedOut, sendJson, sleep } from "../app/api.ts";
import { refreshDrillBadge } from "../app/badge.ts";
import { bandLabel, cap, fmtCount, fmtDate, fmtScore, moveNumberOf, pct, plyLabel } from "../app/format.ts";
import { Icon, type IconName } from "../app/icons.tsx";
import { humanTag } from "../app/kinds.ts";
import { prefs, savePrefs } from "../app/prefs.ts";
import { follow, goBack, navigate, replaceWith, setAddress } from "../app/router.ts";
import { toast } from "../app/toast.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { Loading, Notice, SideMark } from "../app/ui.tsx";
import { Board, type LastMove } from "../board/Board.tsx";
import { REPLY_SHADES, type Tone } from "../board/surface.ts";
import { BookPanel } from "../board/BookPanel.tsx";
import { quirks, quirkDone, quirkSays, quirkTitle, tooFew, type Quirk } from "../board/sly.ts";
import { standardLine } from "../board/lines.ts";
import { engineLine, scoreOf } from "../app/engine.ts";
import { Chess } from "chess.js";
import type { Score } from "../board/lines.ts";
import type { Shown } from "../board/share.ts";
import { BoardMenu, BoardScreen } from "./BoardScreen.tsx";
import "./repertoire.css";
import { Blocks, FLAG_TEXT, HabitNote, HabitWhere, List, MissDot, gamesText } from "./repertoire-parts.tsx";
import {
  type Address, type Habit, type Lines, type RepState, type TreeMove, type TreeNode,
  byMain, epdOf, foldsTo, fromAddress, addressOf, lineEpd, lineFen as lineFenOf, missedLately, outline,
  COMMON_SHARE, groupMoves, movesTo, play, reveal as revealIn, select as selectIn, shown as shownOf, showHabit, showHabitMove, sideToMove, start, stepBack, stepOn, canStepBack, toTried,
} from "./repertoire-view.ts";
import { current, drillFrom, nextStep, stageOf, subtitleOf, titleOf, courseProgress, type Lessons, type Stage } from "./lesson-view.ts";

interface Gap {
  epd: string;
  fen: string;
  move_number: number;
  san: string;
  games: number;
  score?: number | null;
  band_share?: number | null;
}

interface Trap {
  epd: string;
  /** The position the trap reaches, where drilling it starts. */
  to?: string;
  fen: string;
  move_number: number;
  san: string;
  reply_san: string;
  white_eval_after?: Score | null;
  /** What punishes it, from the book's engine line; absent without one. */
  combination?: { sans: string[]; motifs: string[]; wins: { kind: "mate" | "material" | "position"; cents?: number } } | null;
}

/** "Mate in 3 moves · fork": what a trap comes to, without its moves, so the
 *  drill still asks for them. */
function trapSummary(tp: Trap): string {
  const c = tp.combination;
  if (!c) return tp.white_eval_after ? `${fmtScore(tp.white_eval_after)} after your reply` : "";
  const yours = Math.ceil(c.sans.length / 2);
  const cents = c.wins.cents || 0;
  const what =
    c.wins.kind === "mate"
      ? `Mate in ${yours}`
      : c.wins.kind === "material"
        ? `Wins ${cents >= 900 ? "a queen's worth" : cents >= 500 ? "a rook's worth" : cents >= 300 ? "a piece" : cents >= 200 ? `${Math.round(cents / 100)} pawns` : "a pawn"} in ${yours} ${yours === 1 ? "move" : "moves"}`
        : `Wins the position${tp.white_eval_after ? ` (${fmtScore(tp.white_eval_after)})` : ""}`;
  return [what, ...c.motifs.filter((m) => m !== "mate").slice(0, 2).map(humanTag)].join(" · ");
}

interface Data {
  repertoire: { id: number; name: string; color: "white" | "black"; source: string; positions: number; due?: number; new?: number; learned?: number; games?: number };
  tree?: { root: string; nodes: Record<string, TreeNode> };
  cards?: Record<string, { step: number }>;
  habits?: Habit[];
  gaps?: Gap[];
  traps?: Trap[];
  /** The course as lessons (lesson-view.ts); null when its line does not branch. */
  lessons?: Lessons | null;
}





// --- THE SCREEN ---

/** A lesson's stage, as its row says it. */
const STAGE_LABEL: Record<Stage, string> = { new: "", watched: "Watched", tried: "Tried", learned: "Learned" };


export function RepertoireScreen({ params, query }: ScreenProps) {
  const id = params.id!;
  const [data, setData] = useState<Data | null>(null);
  const [error, setError] = useState("");
  // Where the address says the page is: read once, as the page's first state.
  const startAddress = useRef({
    line: query.get("line"),
    tried: query.get("tried"),
    at: query.has("at") ? Number(query.get("at")) : null,
    habit: query.get("habit"),
    off: query.get("off") ? (1 as const) : null,
  });
  const target = useRef<string | null>(null);

  const load = useCallback(async (then?: string | null) => {
    try {
      target.current = then ?? null;
      // The lessons come with the page, so the bar's Learn is there from
      // the first draw rather than arriving under a finger.
      const [page, lessons] = await Promise.all([
        getJson<Data>(`/api/repertoires/${id}`),
        getJson<{ lessons: Lessons | null }>(`/api/repertoires/${id}/lessons`).catch(() => null),
      ]);
      setData({ ...page, lessons: lessons?.lessons ?? null });
    } catch (err) {
      if (!isSignedOut(err)) {
        if (then === undefined) setError(errorText(err));
        else toast(errorText(err));
      }
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const meta = { title: "Repertoire", nav: "openings", back: "/openings", backLabel: "Openings" };
  if (!data) {
    return (
      <div className="screen">
        <BackButton meta={meta} />
        {error ? <Notice kind="error">{error}</Notice> : <Loading text="Opening the repertoire…" />}
      </div>
    );
  }
  return (
    <div className="screen rep-screen">
      <BackButton meta={meta} />
      <Page data={data} reload={(then) => load(then)} startAddress={startAddress.current} target={target} />
    </div>
  );
}

function Page({
  data,
  reload,
  startAddress,
  target,
}: {
  data: Data;
  reload: (then: string | null) => Promise<void>;
  startAddress: Address;
  target: { current: string | null };
}) {
  const rep = data.repertoire;
  const color = rep.color;
  const nodes = data.tree?.nodes || {};
  const root = data.tree?.root || "";
  const cards = data.cards || {};
  const tree = useMemo(() => outline(root, nodes, color), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const lines = useMemo<Lines>(() => ({ root, nodes, outline: tree }), [tree]); // eslint-disable-line react-hooks/exhaustive-deps
  const missed = useMemo(() => missedLately(nodes, data.habits || []), [data]); // eslint-disable-line react-hooks/exhaustive-deps

  // Where the page is between changes: the selected move, which branch
  // points are open, whether all of them are. Every change to the line
  // re-reads the repertoire, and this keeps that from being noticed.
  const [rs, setRs] = useState<RepState>(() => fromAddress(lines, startAddress, data.habits || []));
  const { at, view } = rs;
  const [engineEval, setEngineEval] = useState<{ fen: string; score: Score | null }>({ fen: "", score: null });
  const [thinking, setThinking] = useState(false);
  const [open, setOpen] = useState<Set<string>>(() => new Set(foldsTo(lines, lineEpd(rs))));
  const [all, setAll] = useState(false);
  const [tab, setTab] = useState("");
  const [bookEval, setBookEval] = useState<{ fen: string; score: Score | null; summary: string }>({ fen: "", score: null, summary: "" });
  // What the book knows about the position that the line does not: your own
  // rare moves that score well, or theirs that commonly go wrong.
  const [found, setFound] = useState<{ fen: string; moves: Quirk[]; thin: number }>({ fen: "", moves: [], thin: 0 });
  // Off the line, what comes next as the book sees it: the move it
  // recommends for you, or the reply most played at your level. A move the
  // page suggested does not end in a wall: on goes on with this.
  const [bookNext, setBookNext] = useState<{ fen: string; uci: string; san: string; why: string } | null>(null);
  // What the board was pointing at where it came from, kept one position, so
  // that a move it offered can be named once it has been played.
  const [offered, setOffered] = useState<{ fen: string; moves: Quirk[] }>({ fen: "", moves: [] });
  const [busy, setBusy] = useState("");
  const [bookVersion, setBookVersion] = useState(0);
  const [bookOpen, setBookOpen] = useState(prefs.repBook !== false);
  // The position whose rare replies are unfolded; any other folds them again.
  const [rareOpen, setRareOpen] = useState("");
  // The course as lessons, and the one to go on with.
  const lessonList = data.lessons?.list || [];
  const learning = current(lessonList);
  const progress = courseProgress(lessonList);
  /** A lesson, at its next step: watched, tried from memory, then drilled. */
  const openLesson = (i: number) => {
    const lesson = lessonList[i];
    if (!lesson) return;
    const then = nextStep(lesson);
    const from = drillFrom(lesson);
    if (then?.mode === "drill" && from) return navigate(`/openings/${rep.id}/drill?from=${encodeURIComponent(from)}`);
    navigate(`/openings/${rep.id}/lesson/${i}` + (then?.mode === "try" ? "?mode=try" : ""));
  };

  const shownEpd = at.mv ? at.mv.to : at.nodeEpd;

  /** Show a state of the page, opening the tree's folds to its position. */
  const go = useCallback(
    (next: RepState) => {
      setRs(next);
      const folds = foldsTo(lines, lineEpd(next));
      if (folds.length) setOpen((was) => new Set([...was, ...folds]));
      requestAnimationFrame(() => document.querySelector(".tree .mv.at")?.scrollIntoView({ block: "nearest" }));
    },
    [lines]
  );
  const select = useCallback((nodeEpd: string, mv: TreeMove | null) => go(selectIn(nodeEpd, mv)), [go]);
  const reveal = useCallback((epd: string) => go(revealIn(lines, epd)), [go, lines]);

  // The address says where the page is, from this one place: a reload, Back
  // into the page or the link opened elsewhere comes back to it.
  useEffect(() => {
    setAddress({ ...addressOf(lines, rs) });
  }, [rs, lines]);

  // After a change to the repertoire: to where the change happened, or back
  // to the start when the move that was selected is gone.
  useEffect(() => {
    if (target.current) {
      reveal(target.current);
      target.current = null;
    } else if (at.mv && !(nodes[at.nodeEpd]?.moves || []).some((m) => m.uci === at.mv!.uci)) {
      go(start(lines));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // Changing a line re-reads games against it, which takes a moment; the
  // button says so rather than looking as if the click was lost.
  async function act(key: string, path: string, payload: unknown, done: string | ((d: any) => string), then: string | null) {
    setBusy(key);
    try {
      const res = await sendJson("POST", path, payload);
      toast(typeof done === "function" ? done(res) : done);
      refreshDrillBadge(true);
      await reload(then);
      setBookVersion((n) => n + 1);
    } catch (err) {
      if (!isSignedOut(err)) toast(errorText(err));
    } finally {
      setBusy("");
    }
  }

  /** Build more of the line from a position, or from every line that stops.
   *  Past the book's data the server searches with its engine, which takes a
   *  few seconds, so the button waits on the job. */
  async function deeper(key: string, epd: string | null, label: string) {
    const wording = (n: number) => `${n} more ${n === 1 ? "position" : "positions"} of ${label}.`;
    setBusy(key);
    try {
      const res = await sendJson("POST", `/api/repertoires/${rep.id}/extend`, epd ? { epd } : {});
      if (res.added) toast(wording(res.added));
      else if (res.job_id) {
        toast("Nobody has played on from here. Asking the engine…");
        let job;
        for (;;) {
          job = await getJson(`/api/jobs/${res.job_id}`);
          if (job.status === "done" || job.status === "failed") break;
          await sleep(1200);
        }
        if (job.status === "failed") throw new Error(job.error || "The engine could not search that position.");
        const n = job.result?.added || 0;
        toast(n ? `${wording(n)} From the engine: nobody has played on from here.` : "Nothing more to add here, even for the engine.");
      } else toast("The book has nothing more to add here.");
      refreshDrillBadge(true);
      await reload(epd || shownEpd);
    } catch (err) {
      if (!isSignedOut(err)) toast(errorText(err));
    } finally {
      setBusy("");
    }
  }

  const Plus = ({ epd, cls, title }: { epd: string; cls: string; title: string }) => (
    <button type="button" className={"plus " + cls + (busy === `plus:${epd}` ? " busy" : "")} title={title} aria-label={title} disabled={Boolean(busy)} onClick={() => void deeper(`plus:${epd}`, epd, "this line")}>
      +
    </button>
  );

  // --- what the board shows ---

  const node = nodes[at.nodeEpd];
  const here = nodes[shownEpd];
  const onBoard = shownOf(lines, rs);
  const fen = onBoard.fen;
  const last: LastMove = onBoard.last;
  const bookFen = fen;
  // The line's position under a trial: what the moves tried are tried from.
  const lineFen = lineFenOf(lines, rs);
  const same = (a: string, b: string) => a.split(" ").slice(0, 4).join(" ") === b.split(" ").slice(0, 4).join(" ");
  // The evaluation sits in one place for every position: the move's own when
  // the line carries one, else the book's for the position.
  const known =
    (view.kind === "line" ? at.mv?.white_eval : null) || (bookEval.fen && same(bookEval.fen, fen) ? bookEval.score : null);
  const score = known || (engineEval.fen && same(engineEval.fen, fen) ? engineEval.score : null);

  const next = byMain(here?.moves);
  // The quirks belong to the position on the board, and go the moment it
  // changes.
  const shownFound = found.fen && same(found.fen, fen) ? found.moves : [];
  const yoursNext = Boolean(here) && sideToMove(here!.fen) === color;
  // What is shown of them: all of your moves; of theirs, the few most played
  // at your level and where they go wrong, the rest folded under "Rare".
  const grouped = groupMoves(here?.moves, yoursNext);
  const shownNext = [...grouped.main, ...grouped.wrong, ...(rareOpen === shownEpd ? grouped.rare : [])];
  // What each move is drawn in. A move can be two things at once — a reply
  // the line prepares for and the mistake they most often make — and then it
  // is drawn as the second, since the first is already on the board as an
  // arrow and the chip beside it: one arrow, shimmering, saying both. The
  // dots on the buttons are read from here too, so a dot is always its own
  // arrow's colour.
  const drawnAs = (() => {
    const quirk = (uci: string) => shownFound.find((q) => q.uci === uci)?.kind;
    const ucis = [...shownNext.map((m) => m.uci), ...shownFound.map((q) => q.uci).filter((uci) => !shownNext.some((m) => m.uci === uci))];
    const counted: Partial<Record<Tone, number>> = {};
    const shades = new Map<string, { tone: Tone; shade: number }>();
    for (const uci of ucis) {
      const tone: Tone = quirk(uci) || (yoursNext ? "line" : "reply");
      const shade = (counted[tone] = (counted[tone] ?? -1) + 1) % REPLY_SHADES;
      shades.set(uci, { tone, shade });
    }
    return shades;
  })();
  const onward = next[0];
  const into = tree.reachedBy.get(shownEpd);
  const atStart = !into && shownEpd === root;

  // --- moves made on the board ---

  // What the position on the board is worth where neither the line nor the
  // book says: asked of the engine, once it has stayed a moment. Every
  // position has a score, on the line as much as off it.
  useEffect(() => {
    if (known || !fen) return;
    let live = true;
    const timer = setTimeout(() => {
      scoreOf(fen).then((white) => live && setEngineEval({ fen, score: white }));
    }, 350);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [Boolean(known), fen]); // eslint-disable-line react-hooks/exhaustive-deps

  // Every legal move, for either side: the one whose move it is.
  const legal = useMemo(() => {
    if (view.kind !== "line" && view.kind !== "trial") return [];
    try {
      return new Chess(fen).moves({ verbose: true }).map((m) => m.from + m.to + (m.promotion || ""));
    } catch {
      return [];
    }
  }, [fen, view.kind]);

  /** A move made on the board. On the line, one of the line's moves walks
   *  the line; anything else is tried, from wherever the board is. */
  const played = (uci: string) => {
    if (view.kind === "line" && !next.some((m) => standardLine(lineFen, [m.uci])[0] === uci)) {
      // A move of yours the board suggests — rare, and it scores — is a move
      // worth having: played, it goes into the line, and the line is built
      // on from it.
      const rare = shownFound.find((q) => q.kind === "sly" && standardLine(fen, [q.uci])[0] === uci);
      if (rare && yoursNext) return void takeRare(rare);
    }
    const quirk = shownFound.find((q) => standardLine(fen, [q.uci])[0] === uci);
    const after = play(lines, rs, uci, quirk);
    if (after) go(after);
  };

  async function takeRare(q: Quirk) {
    if (busy) return;
    const game = new Chess(fen);
    const move = game.move({ from: q.uci.slice(0, 2), to: q.uci.slice(2, 4), promotion: q.uci.slice(4, 5) || "q" });
    if (!move) return;
    const child = epdOf(game.fen());
    setBusy(`take:${q.uci}`);
    try {
      await sendJson("POST", `/api/repertoires/${rep.id}/accept`, { epd: shownEpd, san: move.san });
      toast(`${move.san} is part of your line now.`);
      // On it at once, whether or not the book has more to build from it.
      await reload(child);
    } catch (err) {
      setBusy("");
      if (!isSignedOut(err)) toast(errorText(err));
      return;
    }
    await deeper(`take:${q.uci}`, child, `the line after ${move.san}`);
  }

  // What the engine's answer is played into: the board as it is when the
  // answer arrives, not as it was when it was asked.
  const live = useRef({ played, fen });
  live.current = { played, fen };

  /** The engine's move from the position on the board, played. */
  async function engineMove() {
    if (thinking) return;
    const from = fen;
    setThinking(true);
    try {
      const answer = await engineLine(from);
      const best = answer.line?.[0];
      if (answer.error) toast(answer.error);
      else if (!best) toast("The engine has no move here.");
      else if (same(from, live.current.fen)) live.current.played(standardLine(from, [best])[0]!);
      if (answer.white_eval) setEngineEval({ fen: from, score: answer.white_eval });
    } finally {
      setThinking(false);
    }
  }

  // --- the line row: back, the move, on ---

  // Past the last move tried, on plays what the book says comes next, and
  // the engine's move where the book has nothing.
  const trialNext = view.kind === "trial" && view.at === view.steps.length && bookNext && same(bookNext.fen, fen) ? bookNext : null;

  const evalPill = (
    <span
      className={"eval-pill " + (!score ? "none" : score.value < 0 ? "black" : "white")}
      title={score ? "Evaluation of this position, from White's side" : "No evaluation yet: the engine is looking at this position"}
    >
      {score ? fmtScore(score) : "–"}
    </span>
  );
  const stepBtn = (which: "back" | "on", onClick: () => void, title: string, disabled: boolean) => (
    <button type="button" className={"mv-chip step step-" + which} title={title} aria-label={title} disabled={disabled} onClick={onClick}>
      <Icon name={which === "back" ? "stepBack" : "stepOn"} />
    </button>
  );
  let lineRow: ReactNode;
  if (view.kind === "habit") {
    const hn = nodes[view.hb.epd];
    lineRow = (
      <div className="rep-caption-head">
        {stepBtn("back", () => reveal(view.hb.epd), "Back to the line", false)}
        <span className="rep-caption-mid">
          <span className="rep-caption-move">
            <b>Move {moveNumberOf(hn?.ply || 0)}</b>
            <span className="move-mark flag">habit</span>
          </span>
          {evalPill}
        </span>
        {stepBtn("on", () => {}, "", true)}
      </div>
    );
  } else if (view.kind === "trial") {
    const step = view.at > 0 ? view.steps[view.at - 1]! : null;
    const redo = view.at < view.steps.length;
    const ply = (node?.ply ?? 0) + (at.mv ? 1 : 0) + view.at - 1;
    lineRow = (
      <div className="rep-caption-head">
        {stepBtn("back", () => go(stepBack(lines, rs)), view.at > 0 ? "One move back" : "Back to the line", false)}
        <span className="rep-caption-mid">
          <span className="rep-caption-move">
            <b>{step ? `${plyLabel(ply)} ${step.san}` : at.mv ? `${plyLabel(node!.ply)} ${at.mv.san}` : "Start"}</b>
            <span className="move-mark tried">{step ? "tried" : "your line"}</span>
          </span>
          {evalPill}
        </span>
        {stepBtn(
          "on",
          () => (redo ? go(stepOn(lines, rs)!) : trialNext ? played(trialNext.uci) : void engineMove()),
          redo ? "On a move" : trialNext ? `On: ${trialNext.san}, ${trialNext.why}` : "On: the engine's move",
          !redo && (thinking || !legal.length)
        )}
      </div>
    );
  } else if (view.kind === "off") {
    lineRow = (
      <div className="rep-caption-head">
        {stepBtn("back", () => go(stepBack(lines, rs)), "Back to the habit", false)}
        <span className="rep-caption-mid">
          <span className="rep-caption-move">
            <b>
              {plyLabel(nodes[view.hb.epd]?.ply || 0)} {view.hb.played_san}
            </b>
            <span className="move-mark flag">off your line</span>
          </span>
          {evalPill}
        </span>
        {stepBtn("on", () => {}, "", true)}
      </div>
    );
  } else {
    lineRow = (
      <div className="rep-caption-head">
        {stepBtn("back", () => go(stepBack(lines, rs)), atStart ? "This is the start of the line" : "One move back", !canStepBack(lines, rs))}
        <span className="rep-caption-mid">
          <span className="rep-caption-move">
            <b>{at.mv ? `${plyLabel(node!.ply)} ${at.mv.san}${at.mv.nag || ""}` : "Start"}</b>
            {at.mv?.flag ? <span className="move-mark flag">{at.mv.flag}</span> : null}
            {at.mv?.trap ? <span className="move-mark trap">trap</span> : null}
          </span>
          {evalPill}
        </span>
        {stepBtn("on", () => { const on = stepOn(lines, rs); if (on) go(on); }, onward ? `On along the line: ${onward.san}` : "The line stops here", !onward)}
      </div>
    );
  }

  // --- what can be done here ---

  // Drill from the position you are to move in: this one when it is yours,
  // the one after the reply when it is theirs.
  const fromEpd = yoursNext ? shownEpd : next[0]?.to;
  const fromNode = fromEpd ? nodes[fromEpd] : null;
  const isCard = Boolean(fromNode && sideToMove(fromNode.fen) === color && (fromNode.moves || []).length);
  // The move that put this position on the board, where the board had been
  // pointing at it a moment before: walked down the line, or tried off it.
  // A board that offers a move and then says nothing about having played it
  // teaches nothing; this is where the line it pointed at ends.
  const cameBy: Quirk | null = (() => {
    if (view.kind === "trial") return (view.at > 0 && view.steps[view.at - 1]!.quirk) || null;
    if (view.kind !== "line" || !at.mv) return null;
    const from = nodes[at.nodeEpd]?.fen;
    if (!from || !offered.fen || !same(offered.fen, from)) return null;
    return offered.moves.find((q) => standardLine(from, [q.uci])[0] === at.mv!.uci) || null;
  })();

  // Keyed by what the button stands for, never by its label: both sides can
  // play Bxf3 in one line tried, and two buttons with one key are left behind
  // on the page by the next position's buttons.
  const chip = (key: string, label: string, cls: string, onClick: () => void, title?: string, mark?: ReactNode, after?: ReactNode) => (
    <button key={key} type="button" className={"mv-chip" + (cls ? " " + cls : "")} title={title} onClick={onClick}>
      {mark}
      {label}
      {after}
    </button>
  );

  const moveChip = (m: (typeof next)[number]) =>
    chip(
      `line:${m.uci}`,
      m.san + (m.nag || ""),
      (yoursNext ? "yours" : "theirs") + (m.trap ? " trap" : "") + (m.flag ? " flag-" + m.flag : ""),
      () => reveal(m.to),
      [m.comment, !yoursNext && m.share ? `${Math.round(m.share * 100)}% of games at your level play it.` : ""].filter(Boolean).join(" ") || undefined,
      // The shade of its arrow on the board.
      drawnAs.has(m.uci) ? <span className={`reply-dot ${drawnAs.get(m.uci)!.tone}-shade-${drawnAs.get(m.uci)!.shade}`} /> : null,
      // And how often your games have gone wrong down this move lately.
      <MissDot n={missed.beneath(m.to)} title={`Down this line you left it in ${gamesText(missed.beneath(m.to))} in the last month`} />
    );

  const baseIsYours = sideToMove(lineFen) === color;
  const firstTried = view.kind === "trial" ? view.steps[0] : undefined;
  const where: ReactNode =
    view.kind === "trial" ? (
      <div className="rep-where rep-tried">
        <span className="rep-where-says">{firstTried?.quirk ? quirkTitle(firstTried.quirk.kind) : "Off your line"}</span>
        {view.steps.map((step, i) =>
          chip(`tried:${i}:${step.uci}`, step.san, "tried" + (i + 1 === view.at ? " at" : ""), () => go(toTried(rs, i + 1)))
        )}
      </div>
    ) : view.kind === "habit" ? (
      <HabitWhere hb={view.hb} nodes={nodes} chip={chip} reveal={reveal} onOff={() => go(showHabitMove(rs, view.hb))} />
    ) : view.kind === "off" ? null : !here ? null : !next.length ? (
      <div className="rep-where">
        <span className="rep-where-says">The line stops here.</span>
        <Plus epd={shownEpd} cls="mv-chip primary" title="Build the line on from here" />
      </div>
    ) : (
      <div className="rep-where">
        <span className="rep-where-says">{yoursNext ? "Your line plays" : "Prepared for"}</span>
        {grouped.main.map(moveChip)}
        {grouped.wrong.length ? <span className="rep-where-says rep-where-wrong">Where they go wrong</span> : null}
        {grouped.wrong.map(moveChip)}
        {grouped.rare.length ? (
          <>
            {rareOpen === shownEpd ? grouped.rare.map(moveChip) : null}
            <button
              type="button"
              className="mv-chip rep-rare"
              title={rareOpen === shownEpd ? "Show only the replies you will meet most" : "The replies few people play at your level"}
              onClick={() => setRareOpen(rareOpen === shownEpd ? "" : shownEpd)}
            >
              {rareOpen === shownEpd ? "Fewer" : `${grouped.rare.some((m) => (m.share ?? 0) >= COMMON_SHARE) ? "More" : "Rare"} (${grouped.rare.length})`}
            </button>
          </>
        ) : null}
        <Plus epd={shownEpd} cls="mv-chip" title="More moves from this position" />
      </div>
    );

  const notes: ReactNode =
    view.kind === "trial" ? (
      <>
        {firstTried?.quirk ? <p className="rep-note">{quirkSays(firstTried.quirk)}</p> : null}
        <p className="rep-note muted">
          Not in your line. Play on for either side; the evaluation and the book below follow the board.
        </p>
      </>
    ) : view.kind === "habit" ? (
      <HabitNote hb={view.hb} nodes={nodes} />
    ) : view.kind === "off" ? (
      <p className="rep-note">Not in your line: the table below is what the book knows about it.</p>
    ) : (
      <>
        {missed.here(shownEpd) && yoursNext ? (
          <p className="rep-note rep-missed">
            <MissDot n={missed.here(shownEpd)} title="" />
            {`In the last month you played ${missed
              .played(shownEpd)
              .map((p) => `${p.san} here in ${gamesText(p.games)}`)
              .join(" and ")}, not your line.`}
          </p>
        ) : null}
        {at.mv?.comment && !(cameBy && rep.source === "book") ? <p className="muted rep-note">{at.mv.comment}</p> : null}
        {at.mv?.flag ? <p className="faint">{FLAG_TEXT[at.mv.flag] || ""}</p> : null}
      </>
    );

  // What the book knows and no line says: rare moves of your own that score
  // well, common ones of theirs that do not. Each is on the board as a
  // shimmering arrow; pressing one plays it, so it can be looked at and then
  // put into the line with the buttons that are already there.
  const ended: ReactNode = cameBy ? (
    <div className="rep-worth rep-worth-done">
      <div className="rep-worth-title">{quirkDone(cameBy.kind)}</div>
      <div className="rep-worth-move">
        <span className={`reply-dot ${cameBy.kind}-shade-0`} />
        <span className="rep-worth-san">{cameBy.san}</span>
        <span className="rep-worth-says">
          {quirkSays(cameBy, true)}
          {score ? ` The board is ${fmtScore(score)}.` : ""}
        </span>
      </div>
    </div>
  ) : null;

  // Nothing to point at, and too few games to be sure there is nothing: the
  // difference between the book having looked and the book having nothing
  // to look at.
  const thin: ReactNode =
    !shownFound.length && found.thin && found.fen && same(found.fen, fen) ? (
      <p className="rep-note muted rep-thin">{fmtCount(found.thin)} games reach this position — too few to tell a mistake from noise.</p>
    ) : null;

  const worth: ReactNode = shownFound.length ? (
    <div className="rep-worth">
      <div className="rep-worth-title">{quirkTitle(shownFound[0]!.kind)}</div>
      {shownFound.map((q) => (
        <button
          key={q.uci}
          type="button"
          className="rep-worth-move"
          title={q.kind === "sly" && view.kind === "line" ? `Add ${q.san} to your line and build it on` : q.kind === "sly" ? `Try ${q.san} on the board` : `Try ${q.san} for them on the board`}
          onClick={() => played(q.uci)}
        >
          <span className={`reply-dot ${drawnAs.get(q.uci)?.tone || q.kind}-shade-${drawnAs.get(q.uci)?.shade ?? 0}`} />
          <span className="rep-worth-san">{q.san}</span>
          <span className="rep-worth-says">
            {next.some((m) => m.uci === q.uci) ? <span className="rep-worth-tag">{yoursNext ? "in your line" : "prepared for"}</span> : null}
            {quirkSays(q)}
          </span>
        </button>
      ))}
    </div>
  ) : null;

  const actionsHere =
    view.kind === "trial" ? (
      <div className="rep-here-actions">
        <button type="button" className="btn btn-sm" disabled={thinking || !legal.length} onClick={() => void engineMove()}>
          <Icon name="skip" />
          {thinking ? "Thinking…" : "Engine's move"}
        </button>
        {firstTried ? (
          <button
            type="button"
            className="btn btn-sm"
            title={
              baseIsYours
                ? `Make ${firstTried.san} your move here`
                : `Prepare for ${firstTried.san} here, with the book's answer to it`
            }
            disabled={Boolean(busy)}
            onClick={() =>
              void act(
                "add-tried",
                `/api/repertoires/${rep.id}/${baseIsYours ? "accept" : "prepare"}`,
                { epd: shownEpd, san: firstTried.san },
                baseIsYours ? `${firstTried.san} is part of your line now.` : `Prepared for ${firstTried.san}.`,
                epdOf(firstTried.fen)
              )
            }
          >
            <Icon name="branch" />
            {busy === "add-tried" ? "Saving…" : baseIsYours ? `Add ${firstTried.san} to my line` : `Prepare for ${firstTried.san}`}
          </button>
        ) : null}
        <button
          type="button"
          className="btn btn-sm"
          title="Play on from this position against the bot"
          onClick={() => {
            const ucis = [...movesTo(lines, shownEpd).map((m) => m.uci), ...view.steps.slice(0, view.at).map((step) => step.uci)];
            navigate(`/openings/${rep.id}/play` + (ucis.length ? `?moves=${ucis.join(",")}` : ""));
          }}
        >
          <Icon name="play" />
          Play from here
        </button>
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => select(at.nodeEpd, at.mv)}>
          <Icon name="back" />
          Back to line
        </button>
      </div>
    ) : view.kind === "off" ? null : (
      <div className="rep-here-actions">
        {isCard ? (
          <button type="button" className="btn btn-sm" onClick={() => navigate(`/openings/${rep.id}/drill?from=${encodeURIComponent(fromEpd!)}`)}>
            <Icon name="target" />
            Drill from here
          </button>
        ) : null}
        {/* A game against the bot from the position on the board. */}
        <button
          type="button"
          className="btn btn-sm"
          title="Play this opening against the bot from this position"
          onClick={() => {
            const ucis = movesTo(lines, shownEpd).map((m) => m.uci);
            navigate(`/openings/${rep.id}/play` + (ucis.length ? `?moves=${ucis.join(",")}` : ""));
          }}
        >
          <Icon name="play" />
          Play from here
        </button>
        {prefs.repTree ? (
          <button
            type="button"
            className="btn btn-sm"
            title="This position in the tree of lines"
            onClick={() => {
              setTab("lines");
              reveal(shownEpd);
            }}
          >
            <Icon name="branch" />
            Lines
          </button>
        ) : null}
        {/* A move can be taken out of the line, with whatever only it
            reached — unless it is the only move there, which would cut the
            line off rather than trim it. */}
        {view.kind === "line" && at.mv && (node?.moves || []).length > 1 ? (
          <button
            type="button"
            className="btn btn-sm btn-ghost btn-danger"
            title={`Take ${at.mv.san} out of the line`}
            disabled={Boolean(busy)}
            onClick={() => {
              const mv = at.mv!;
              const below = (nodes[mv.to]?.moves || []).length ? " and everything after it" : "";
              if (!confirm(`Remove ${mv.san}${below} from this line?`)) return;
              void act("remove", `/api/repertoires/${rep.id}/remove`, { epd: at.nodeEpd, san: mv.san }, `${mv.san} is out of the line.`, at.nodeEpd);
            }}
          >
            <Icon name="trash" />
            {busy === "remove" ? "Saving…" : "Remove"}
          </button>
        ) : null}
      </div>
    );

  // --- the lists ---

  const showLabel = prefs.repTree ? "Lines" : "Show";
  const showIcon: IconName = prefs.repTree ? "branch" : "eye";
  /** Put a position on the board, with the line's move from it selected. */
  const linesAt = (epd: string, exact?: boolean) => {
    if (prefs.repTree) setTab("lines");
    const n = nodes[epd];
    const main = (n?.moves || []).find((m) => m.main) || n?.moves?.[0];
    reveal(exact || !main ? epd : main.to);
  };
  const title = (fenOf: string, moveNumber: number, san: string, rest?: string) => (
    <div className="rep-item-title">
      {moveNumber}
      {sideToMove(fenOf) === "black" ? "…" : "."} {san}
      {rest ? <span className="faint">{rest}</span> : null}
    </div>
  );
  /** "+0.42 → +0.11 on average", from White's side, like every evaluation on screen. */
  const costText = (hb: Habit) => {
    const points = Math.round(hb.avg_lost * 50);
    if (hb.avg_before == null || hb.avg_after == null) return points >= 1 ? "costs you" : "costs nothing";
    const cp = (v: number) => fmtScore({ kind: "cp", value: v });
    return points >= 1 ? `${cp(hb.avg_before)} → ${cp(hb.avg_after)} on average` : `costs nothing (${cp(hb.avg_before)} → ${cp(hb.avg_after)})`;
  };
  const habits = data.habits || [];
  const gaps = data.gaps || [];
  const traps = data.traps || [];
  const costly = habits.filter((hb) => Math.round(hb.avg_lost * 50) >= 2).length;

  const panels: Record<string, ReactNode> = {
    lessons: (
      <div className="card rep-list rep-lessons">
        <div className="rep-list-head">
          <h2>Learn it in {lessonList.length} lessons</h2>
          <span className="faint">
            {progress.learned} of {progress.total} learned
          </span>
        </div>
        <ol className="lesson-list">
          {lessonList.map((l, i) => {
            const stage = stageOf(l);
            const then = nextStep(l);
            return (
              <li key={l.key}>
                <button type="button" className={"lesson-row" + (i === learning ? " current" : "")} onClick={() => openLesson(i)}>
                  <span className="lesson-num num">{i + 1}</span>
                  <span className="lesson-row-text">
                    <b>{titleOf(l, rep.name)}</b>
                    <span className="faint">{subtitleOf(l)}</span>
                  </span>
                  <span className={"lesson-stage stage-" + stage}>{i === learning && then ? then.label : STAGE_LABEL[stage]}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </div>
    ),
    lines: prefs.repTree ? (
      <div className="card rep-tree">
        <div className="rep-list-head">
          <h2>{tree.branchPoints ? `Main line · ${tree.branchPoints} branch${tree.branchPoints === 1 ? "" : "es"}` : "Line"}</h2>
          {tree.branchPoints ? (
            <button
              type="button"
              className="link-btn fold-all"
              onClick={() => {
                if (all) setOpen(new Set());
                setAll(!all);
              }}
            >
              {all ? "Main line only" : "Show all lines"}
            </button>
          ) : null}
        </div>
        <div className="tree">
          {root ? (
            <Blocks
              blocks={tree.blocks}
              open={(k) => all || open.has(k)}
              toggle={(k) =>
                setOpen((was) => {
                  const now = new Set(was);
                  if (all || now.has(k)) now.delete(k);
                  else now.add(k);
                  if (all) setAll(false);
                  return now;
                })
              }
              at={at}
              color={color}
              cards={cards}
              missedAt={missed.here}
              onSelect={(nodeEpd, mv) => select(nodeEpd, mv)}
              plus={(epd) => <Plus epd={epd} cls="grow-line" title="Build this line on from here" />}
            />
          ) : (
            <p className="faint">This repertoire has no moves.</p>
          )}
        </div>
      </div>
    ) : null,
    habits: (
      <List
        title="Habits"
        sub="Moves your games keep playing instead of your line. Click one to see it."
        action={
          habits.length >= 2 ? (
            <button
              type="button"
              className={"btn btn-sm btn-ghost" + (costly ? " btn-danger" : "")}
              title="Accept every one of these moves into your line"
              disabled={Boolean(busy)}
              onClick={() => {
                if (
                  !confirm(
                    `Take all ${habits.length} of these moves into your line?${costly ? ` ${costly} of them cost you in your games.` : ""} They become moves the drill accepts, and they stop counting as habits.`
                  )
                )
                  return;
                void act("grab-all", `/api/repertoires/${rep.id}/accept`, { moves: habits.map((hb) => ({ epd: hb.epd, san: hb.played_san })) }, `${habits.length} moves are part of your line now.`, null);
              }}
            >
              {busy === "grab-all" ? "Saving…" : `Grab all ${habits.length}`}
            </button>
          ) : null
        }
        items={habits.map((hb) => {
          const cost = Math.round(hb.avg_lost * 50);
          return (
            <div key={`${hb.epd}|${hb.played_san}`} className="rep-item">
              <button
                type="button"
                className="rep-item-main"
                title="Show it on the board"
                onClick={() => go(showHabit(lines, hb))}
              >
                {title(hb.fen, hb.move_number, hb.played_san, ` instead of ${(hb.prepared || []).join(" or ")}`)}
                <div className="rep-item-sub">{[gamesText((hb.games || []).length), costText(hb)].join(" · ")}</div>
              </button>
              <div className="rep-item-actions">
                <span className="rep-item-dates">
                  {(hb.games || []).slice(0, 3).map((g) => (
                    <a key={g.game_id + g.color} {...follow(`/games/${g.game_id}/${g.color}`)}>
                      {g.played_at ? fmtDate(g.played_at) : "Game"}
                    </a>
                  ))}
                </span>
                <button type="button" className="btn btn-sm" title="Drill this position" onClick={() => navigate(`/openings/${rep.id}/drill?from=${encodeURIComponent(hb.epd)}`)}>
                  <Icon name="target" />
                  Drill
                </button>
                <button type="button" className="btn btn-sm" title="Put this position on the board" onClick={() => linesAt(hb.epd)}>
                  <Icon name={showIcon} />
                  {showLabel}
                </button>
                {/* Taking the habit into the line is the rare choice, and a
                    bad one when the move costs: the button says so. */}
                <button
                  type="button"
                  className={"btn btn-sm btn-ghost" + (cost >= 2 ? " btn-danger" : "")}
                  title={`Accept ${hb.played_san} into your line${cost >= 2 ? " — it costs you in your games" : ""}`}
                  disabled={Boolean(busy)}
                  onClick={() => void act(`grab:${hb.epd}`, `/api/repertoires/${rep.id}/accept`, { epd: hb.epd, san: hb.played_san }, `${hb.played_san} is part of your line now.`, hb.epd)}
                >
                  {busy === `grab:${hb.epd}` ? "Saving…" : "Grab"}
                </button>
              </div>
            </div>
          );
        })}
      />
    ),
    gaps: (
      <List
        title="Gaps"
        sub="Replies your opponents play that your line does not cover."
        items={gaps.map((gp) => (
          <div key={`${gp.epd}|${gp.san}`} className="rep-item">
            <button type="button" className="rep-item-main" title="Show it on the board" onClick={() => reveal(gp.epd)}>
              {title(gp.fen, gp.move_number, gp.san, " · nothing prepared")}
              <div className="rep-item-sub">
                {[gamesText(gp.games), gp.score != null ? `you scored ${pct(gp.score)}` : "", gp.band_share != null ? `${pct(gp.band_share)} of games at your level` : ""]
                  .filter(Boolean)
                  .join(" · ")}
              </div>
            </button>
            <div className="rep-item-actions">
              <button type="button" className="btn btn-sm" title="Put this position on the board" onClick={() => linesAt(gp.epd, true)}>
                <Icon name={showIcon} />
                {showLabel}
              </button>
              <button
                type="button"
                className="btn btn-sm"
                title={`Add ${gp.san} to the line with the book's answer to it`}
                disabled={Boolean(busy)}
                onClick={() => void act(`prep:${gp.epd}|${gp.san}`, `/api/repertoires/${rep.id}/prepare`, { epd: gp.epd, san: gp.san }, `Prepared a reply to ${gp.san}.`, gp.epd)}
              >
                <Icon name="book" />
                {busy === `prep:${gp.epd}|${gp.san}` ? "Saving…" : "Prepare"}
              </button>
            </div>
          </div>
        ))}
      />
    ),
    traps: (
      <List
        title="Traps"
        sub="Natural-looking replies that lose, with your answer prepared."
        items={traps.map((tp) => (
          <div key={`${tp.epd}|${tp.san}`} className="rep-item">
            <button type="button" className="rep-item-main" title="Show it on the board" onClick={() => reveal(tp.epd)}>
              {title(tp.fen, tp.move_number, `${tp.san}?`, ` ${tp.reply_san}`)}
              <div className="rep-item-sub">{trapSummary(tp)}</div>
            </button>
            <div className="rep-item-actions">
              <button type="button" className="btn btn-sm" title="Find the win after this trap" onClick={() => navigate(`/openings/${rep.id}/drill?from=${encodeURIComponent(tp.to || tp.epd)}`)}>
                <Icon name="target" />
                Drill
              </button>
              <button type="button" className="btn btn-sm" title="Put this trap on the board" onClick={() => linesAt(tp.epd)}>
                <Icon name={showIcon} />
                {showLabel}
              </button>
            </div>
          </div>
        ))}
      />
    ),
  };
  // The tree of every move is for strong players, and off unless asked for.
  const tabDefs = (
    [
      lessonList.length ? ["lessons", "Lessons", lessonList.length] : null,
      prefs.repTree ? ["lines", "Lines", null] : null,
      ["habits", "Habits", habits.length],
      ["gaps", "Gaps", gaps.length],
      ["traps", "Traps", traps.length],
    ] as ([string, string, number | null] | null)[]
  ).filter((t): t is [string, string, number | null] => Boolean(t) && (t![2] == null || t![2] > 0));
  const shownTab = tabDefs.some(([k]) => k === tab) ? tab : tabDefs[0]?.[0] || "";

  // What can be taken out in one go: the moves put in by hand, and the moves
  // the book calls losing — never a position's only move.
  const added: { epd: string; san: string }[] = [];
  const losing: { epd: string; san: string; flag: string }[] = [];
  for (const [epd, n] of Object.entries(nodes)) {
    if (sideToMove(n.fen) !== color || (n.moves || []).length < 2) continue;
    for (const m of n.moves || []) {
      if (m.flag) losing.push({ epd, san: m.san, flag: m.flag });
      if (m.added || (rep.source === "book" && !m.main && !m.comment)) added.push({ epd, san: m.san });
    }
  }
  const removeAll = (list: { epd: string; san: string; flag?: string }[], what: string, done: string) => {
    const names = list.map((l) => l.san + (l.flag ? ` (${l.flag})` : "")).join(", ");
    if (!confirm(`Remove ${list.length} ${what}, with everything after them?\n\n${names}`)) return;
    void act("remove-all", `/api/repertoires/${rep.id}/remove`, { moves: list.map((l) => ({ epd: l.epd, san: l.san })) }, done, null);
  };

  // What is on the board, for the formats it can be copied in: the position
  // the line has been walked to, the moves the line took to get there, and
  // whatever was tried off it.
  const shown = (): Shown => {
    const hb = view.kind === "habit" || view.kind === "off" ? view.hb : null;
    const moves = movesTo(lines, hb ? hb.epd : shownEpd).map((m) => m.uci);
    if (hb && view.kind === "off") moves.push(hb.played_uci);
    if (view.kind === "trial") moves.push(...view.steps.slice(0, view.at).map((step) => step.uci));
    // With no way back to the root — a position the tree does not reach —
    // the moves are no line, and the position stands for itself.
    return {
      fen,
      start: moves.length ? nodes[root]?.fen || fen : fen,
      moves,
      headers: { Event: `Blundermine — ${rep.name}`, Site: location.href },
    };
  };

  const menu = (
    <BoardMenu
      shown={shown}
      items={[
        { label: "Go deeper everywhere", onClick: () => void deeper("deeper", null, "your lines") },
        added.length
          ? {
              label: `Remove moves you added (${added.length})`,
              onClick: () => removeAll(added, "moves you added to the line", `${added.length} ${added.length === 1 ? "move is" : "moves are"} out of the line.`),
            }
          : null,
        losing.length
          ? {
              label: `Remove losing moves (${losing.length})`,
              onClick: () => removeAll(losing, "of your moves the book calls losing", `${losing.length} losing ${losing.length === 1 ? "move is" : "moves are"} out of the line.`),
            }
          : null,
        rep.source === "pgn" || rep.source === "study" ? { label: "Re-import", onClick: () => navigate(`/openings/import?replace=${rep.id}`) } : null,
        {
          label: "Delete",
          danger: true,
          onClick: async () => {
            if (!confirm(`Delete “${rep.name}”? Its schedule goes with it.`)) return;
            try {
              await sendJson("DELETE", `/api/repertoires/${rep.id}`);
              refreshDrillBadge(true);
              // In its place, so that Back does not open what is gone.
              replaceWith("/openings");
            } catch (err) {
              if (!isSignedOut(err)) toast(errorText(err));
            }
          },
        },
      ]}
    />
  );

  // The course: its name, then its lessons, traps, habits and gaps, a tab
  // each. Opening a course is coming for these, so at the start of the line
  // they lead the sheet, on a phone on its first screen; walking into the
  // line, what is said about the position comes first.
  const course = (
    <>
      <div className="game-head rep-head">
        <div>
          <h1>{rep.name}</h1>
          <div className="meta">
            <SideMark color={color} />
            <span>
              {cap(color)}
              {rep.source === "book" ? " · course" : ""}
            </span>
            <span className="faint">·</span>
            <span>{fmtCount(rep.positions)} positions</span>
            <span className="faint">·</span>
            <span>
              {rep.due || 0} due · {rep.new || 0} new · {rep.learned || 0} learned
            </span>
            {rep.games ? (
              <>
                <span className="faint">·</span>
                <span>seen in {fmtCount(rep.games)} games</span>
              </>
            ) : null}
          </div>
        </div>
      </div>
      {tabDefs.length ? (
        <div className="rep-main">
          <div className="tabs" role="tablist">
            {tabDefs.map(([key, label, n]) => (
              <button key={key} type="button" role="tab" className="tab-btn" data-tab={key} aria-selected={shownTab === key} onClick={() => setTab(key)}>
                {label}
                {n != null ? <span className="tab-count num">{n}</span> : null}
              </button>
            ))}
          </div>
          {panels[shownTab]}
        </div>
      ) : null}
    </>
  );
  const courseFirst = atStart && view.kind === "line";

  return (
    <BoardScreen
      className="rep-board-screen"
      board={
        <div className="board-zone">
          <Board
            className="view-board"
            fen={fen}
            orientation={color}
            last={last}
            // What the line offers from here, on the board as arrows: your
            // own moves green, the replies you are prepared for red.
            replies={[
              ...(view.kind === "line" && here
                ? // At most three of their replies as arrows — the most played — so
                  // the board shows the position rather than a web over it; the
                  // rest are the chips under it.
                  (yoursNext ? shownNext : rareOpen === shownEpd ? shownNext : grouped.main.slice(0, 3)).map((m) => ({ uci: m.uci, label: m.san + (m.nag || ""), tone: drawnAs.get(m.uci)?.tone }))
                : []),
              // Tried off the line: what on would play, so it is seen first.
              ...(trialNext ? [{ uci: trialNext.uci, label: trialNext.san, tone: (sideToMove(fen) === color ? "line" : "reply") as Tone }] : []),
              // And what the book knows and the line does not, shimmering.
              ...shownFound.filter((q) => !next.some((m) => m.uci === q.uci)).map((q) => ({ uci: q.uci, label: q.san, tone: q.kind })),
            ]}
            tone={yoursNext ? "line" : "reply"}
            // Tapping where an arrow points is pressing that reply's button;
            // a move the line does not have is tried on the board.
            onReply={(uci) => {
              const reply = next.find((m) => m.uci === uci);
              if (reply) return reveal(reply.to);
              if (shownFound.some((q) => q.uci === uci) || trialNext?.uci === uci) played(uci);
            }}
            // Any legal move, for whichever side is to move: a move of the
            // line walks it, anything else is tried.
            input={legal.length ? { legal, onMove: played } : null}
          />
        </div>
      }
      line={<div className="rep-caption">{lineRow}</div>}
      bar={
        <>
          <button type="button" className="btn menu-btn" onClick={() => goBack("/openings")}>
            <Icon name="back" />
            Back
          </button>
          {lessonList.length ? (
            <button
              type="button"
              className={"btn rep-learn" + (learning != null ? " btn-primary" : "")}
              title={learning != null ? `Lesson ${learning + 1} of ${lessonList.length}` : "Every lesson is learned: go through them again"}
              onClick={() => openLesson(learning ?? 0)}
            >
              <Icon name="book" />
              Learn
            </button>
          ) : null}
          <button type="button" className={"btn" + (learning == null ? " btn-primary" : "")} onClick={() => navigate(`/openings/${rep.id}/drill`)}>
            <Icon name="target" />
            {rep.due ? (
              <>
                Drill<span className="long"> {rep.due} due</span>
              </>
            ) : (
              "Drill"
            )}
          </button>
          <button type="button" className="btn" title="Play this opening against a bot of a level you choose" onClick={() => navigate(`/openings/${rep.id}/play`)}>
            <Icon name="play" />
            Play
          </button>
          {menu}
        </>
      }
      sheet={
        <>
          {courseFirst ? course : null}
          {where}
          {/* Off the line, what is done with the moves tried comes before
              what is said about them: it is pressed again and again. */}
          {view.kind === "trial" ? actionsHere : null}
          {ended}
          <div className="rep-notes">{notes}</div>
          {worth}
          {thin}
          {view.kind === "trial" ? null : actionsHere}
          {courseFirst ? null : course}
          <details
            className="rep-book"
            open={bookOpen}
            onToggle={(e) => {
              const now = (e.currentTarget as HTMLDetailsElement).open;
              prefs.repBook = now;
              savePrefs();
              setBookOpen(now);
            }}
          >
            <summary title="Show or hide what the book knows about this position">
              <span className="rep-book-title">In the book</span>
              <span className="rep-book-sub">{bookEval.summary}</span>
              <Icon name="chev" className="rep-book-chev" />
            </summary>
            <div className="rep-book-body">
              <BookPanel
                fen={bookFen}
                version={bookVersion}
                onData={(position, band) => {
                  const at = position.fen || bookFen;
                  setBookEval({
                    fen: at,
                    score: position.white_eval || null,
                    summary: position.in_book
                      ? [`${fmtCount(position.games?.band)} games at ${bandLabel(band) || "your level"}`, position.white_eval ? fmtScore(position.white_eval) : ""].filter(Boolean).join(" · ")
                      : "not in the book",
                  });
                  // Yours to move: the rare moves that score well. Theirs:
                  // the common ones that go badly for them.
                  if (found.fen && !same(found.fen, at)) setOffered({ fen: found.fen, moves: found.moves });
                  setFound({ fen: at, moves: quirks(position, sideToMove(at) === color ? "sly" : "trap"), thin: tooFew(position) });
                  const mine = sideToMove(at) === color;
                  const pick = mine
                    ? position.recommended?.[0]
                    : (position.moves || []).slice().sort((a, b) => (b.band?.games || 0) - (a.band?.games || 0))[0];
                  setBookNext(
                    pick
                      ? { fen: at, uci: standardLine(at, [pick.uci])[0] || pick.uci, san: pick.san, why: mine ? "the book's move for you" : "their most played reply at your level" }
                      : null
                  );
                }}
                // What the line already has is ticked rather than offered.
                inLine={(uci) => view.kind === "line" && next.some((m) => standardLine(lineFen, [m.uci])[0] === standardLine(lineFen, [uci])[0])}
                // The book can put a move into the line: your move at your
                // turn, a reply to be ready for at theirs, with your answer.
                onAdd={
                  view.kind === "line"
                    ? (mv, position) => {
                        const yours = sideToMove(position.fen) === color;
                        void act(
                          `book:${mv.uci}`,
                          `/api/repertoires/${rep.id}/${yours ? "accept" : "prepare"}`,
                          { epd: shownEpd, san: mv.san },
                          yours ? `${mv.san} is part of your line now.` : `Prepared a reply to ${mv.san}.`,
                          shownEpd
                        );
                      }
                    : undefined
                }
              />
            </div>
          </details>
        </>
      }
    />
  );
}
